(() => {
    "use strict";

    const QUOTE_FAVORITES_STORAGE_KEY =
        "fr-books-quote-favorites:v1";

    const COLLECTION_STATE_KEY =
        "fr-collection-state:v1";

    const SEARCH_MANIFEST_URL =
        "/assets/data/search/manifest.json";


    // ==========================================================
    // PAGE
    // ==========================================================

    const collection =
        document.querySelector(
            "[data-collection-page]"
        ) ||
        document.querySelector(
            ".fr-collection"
        );

    if (
        !collection ||
        !window.frMember
    ) {
        return;
    }


    // ==========================================================
    // ELEMENTS
    // ==========================================================

    const content =
        collection.querySelector(
            "[data-collection-content]"
        );

    const count =
        collection.querySelector(
            "[data-collection-count]"
        );

    const encouragement =
        collection.querySelector(
            "[data-collection-encouragement]"
        );

    const tabs =
        Array.from(
            collection.querySelectorAll(
                "[data-collection-tab]"
            )
        );

    const panels =
        Array.from(
            collection.querySelectorAll(
                "[data-collection-panel]"
            )
        );


    // ==========================================================
    // PIECES FILTER ELEMENTS
    // ==========================================================

    const writingBookSelect =
        collection.querySelector(
            "[data-collection-writing-book]"
        );

    const writingSearchInput =
        collection.querySelector(
            "[data-collection-writing-search]"
        );

    const writingSearchClear =
        collection.querySelector(
            "[data-collection-writing-search-clear]"
        );


    // ==========================================================
    // QUOTES ELEMENTS
    // ==========================================================

    const quotesContent =
        collection.querySelector(
            "[data-collection-quotes]"
        );

    const quotesBookSelect =
        collection.querySelector(
            "[data-collection-quotes-book]"
        );

    const quotesSearchInput =
        collection.querySelector(
            "[data-collection-quotes-search]"
        );

    const quotesSearchClear =
        collection.querySelector(
            "[data-collection-quotes-search-clear]"
        );


    if (
        !content ||
        !count ||
        !encouragement
    ) {
        return;
    }


    // ==========================================================
    // STATE
    // ==========================================================

    let currentView =
        "writing";

    let currentWritingBook =
        "";

    let currentWritingSearch =
        "";

    let currentQuoteBook =
        "";

    let currentQuoteSearch =
        "";

    let pendingWritingRemovalIds =
        new Set();

    let pendingQuoteRemovalIds =
        new Set();

    let searchManifestPromise =
        null;

    const searchCatalogPromises =
        new Map();

    const searchTextByItemId =
        new Map();

    let writingSearchRequestId =
        0;

    let writingSearchLoading =
        false;


    // ==========================================================
    // SESSION STATE
    // ==========================================================

    function readCollectionState() {
        try {
            const raw =
                window.sessionStorage.getItem(
                    COLLECTION_STATE_KEY
                );

            if (!raw) {
                return {};
            }

            const parsed =
                JSON.parse(raw);

            return (
                parsed &&
                typeof parsed === "object" &&
                !Array.isArray(parsed)
            )
                ? parsed
                : {};

        } catch (error) {
            console.warn(
                "Unable to read Favorites page state:",
                error
            );

            return {};
        }
    }


    function writeCollectionState(changes) {
        try {
            const current =
                readCollectionState();

            window.sessionStorage.setItem(
                COLLECTION_STATE_KEY,
                JSON.stringify({
                    ...current,
                    ...changes
                })
            );

        } catch (error) {
            console.warn(
                "Unable to save Favorites page state:",
                error
            );
        }
    }


    // ==========================================================
    // HELPERS
    // ==========================================================

    function escapeHtml(value) {
        return String(value || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }


    function normalizeText(value) {
        return String(value || "")
            .normalize("NFKC")
            .replace(/\u00a0/g, " ")
            .replace(/\s+/g, " ")
            .trim();
    }


    function normalizeSearchText(value) {
        return normalizeText(
            value
        ).toLowerCase();
    }


    function getBookName(item) {
        return (
            normalizeText(
                item?.book
            ) ||
            "Other"
        );
    }


    function getBookKey(value) {
        return normalizeSearchText(
            value
        );
    }


    function sortFavorites(items) {
        return [...items].sort(
            (a, b) => {

                const bookCompare =
                    getBookName(a)
                        .localeCompare(
                            getBookName(b),
                            undefined,
                            {
                                sensitivity:
                                    "base"
                            }
                        );

                if (
                    bookCompare !== 0
                ) {
                    return bookCompare;
                }

                return String(
                    a?.title || ""
                ).localeCompare(
                    String(
                        b?.title || ""
                    ),
                    undefined,
                    {
                        sensitivity:
                            "base"
                    }
                );
            }
        );
    }


    function groupByBook(items) {
        const groups =
            new Map();

        items.forEach(
            (item) => {

                const book =
                    getBookName(
                        item
                    );

                if (
                    !groups.has(book)
                ) {
                    groups.set(
                        book,
                        []
                    );
                }

                groups
                    .get(book)
                    .push(item);
            }
        );

        return groups;
    }


    // ==========================================================
    // ITEM CATALOG
    // ==========================================================

    function getItemCatalog() {
        return (
            window.frItemCatalog ||
            null
        );
    }


    function getCatalogItems() {
        const catalog =
            getItemCatalog();

        if (
            !catalog ||
            typeof catalog.getItems !==
            "function"
        ) {
            return [];
        }

        return (
            catalog.getItems() ||
            []
        );
    }


    function getFavoriteChapterSlug(item) {
        const value =
            String(
                item?.url || ""
            ).trim();

        if (!value) {
            return "";
        }

        try {
            const url =
                new URL(
                    value,
                    window.location.origin
                );

            return normalizeText(
                url.searchParams.get(
                    "chapter"
                )
            );

        } catch {
            return "";
        }
    }


    function getFavoritePathname(item) {
        const value =
            String(
                item?.url || ""
            ).trim();

        if (!value) {
            return "";
        }

        try {
            return new URL(
                value,
                window.location.origin
            ).pathname;

        } catch {
            return "";
        }
    }


    // ==========================================================
    // RESOLVE FAVORITE TO ITEM CATALOG RECORD
    // ==========================================================

    function getCatalogItemForFavorite(item) {
        const catalog =
            getItemCatalog();

        const catalogItems =
            getCatalogItems();

        if (
            !catalogItems.length
        ) {
            return null;
        }


        // ------------------------------------------------------
        // STORED FAVORITE ID
        // ------------------------------------------------------

        const postId =
            normalizeText(
                item?.postId
            );

        if (postId) {

            if (
                typeof catalog?.getItem ===
                "function"
            ) {
                const result =
                    catalog.getItem(
                        postId
                    );

                if (result) {
                    return result;
                }
            }


            if (
                typeof catalog?.getItemById ===
                "function"
            ) {
                const result =
                    catalog.getItemById(
                        postId
                    );

                if (result) {
                    return result;
                }
            }


            if (
                typeof catalog?.getItemBySlug ===
                "function"
            ) {
                const result =
                    catalog.getItemBySlug(
                        postId
                    );

                if (result) {
                    return result;
                }
            }


            const byId =
                catalogItems.find(
                    (catalogItem) =>
                        normalizeText(
                            catalogItem?.id
                        ) ===
                        postId
                );

            if (byId) {
                return byId;
            }


            const bySlug =
                catalogItems.find(
                    (catalogItem) =>
                        normalizeText(
                            catalogItem?.slug
                        ) ===
                        postId
                );

            if (bySlug) {
                return bySlug;
            }
        }


        // ------------------------------------------------------
        // READER URL CHAPTER SLUG
        // ------------------------------------------------------

        const chapterSlug =
            getFavoriteChapterSlug(
                item
            );

        if (chapterSlug) {

            if (
                typeof catalog?.getItemBySlug ===
                "function"
            ) {
                const result =
                    catalog.getItemBySlug(
                        chapterSlug
                    );

                if (result) {
                    return result;
                }
            }


            const byChapterSlug =
                catalogItems.find(
                    (catalogItem) =>
                        normalizeText(
                            catalogItem?.slug
                        ) ===
                        chapterSlug
                );

            if (byChapterSlug) {
                return byChapterSlug;
            }
        }


        // ------------------------------------------------------
        // DIRECT URL PATH
        // ------------------------------------------------------

        const favoritePath =
            getFavoritePathname(
                item
            );

        if (
            favoritePath &&
            favoritePath !==
            "/books/"
        ) {
            const byPath =
                catalogItems.find(
                    (catalogItem) => {

                        const catalogUrl =
                            String(
                                catalogItem?.url ||
                                ""
                            ).trim();

                        if (!catalogUrl) {
                            return false;
                        }

                        try {
                            return (
                                new URL(
                                    catalogUrl,
                                    window.location.origin
                                ).pathname ===
                                favoritePath
                            );

                        } catch {
                            return false;
                        }
                    }
                );

            if (byPath) {
                return byPath;
            }
        }


        // ------------------------------------------------------
        // TITLE + BOOK FALLBACK
        // ------------------------------------------------------

        const favoriteTitle =
            normalizeSearchText(
                item?.title
            );

        const favoriteBook =
            getBookKey(
                item?.book
            );

        if (favoriteTitle) {
            const byTitleBook =
                catalogItems.find(
                    (catalogItem) => {

                        const catalogTitle =
                            normalizeSearchText(
                                catalogItem?.title
                            );

                        if (
                            catalogTitle !==
                            favoriteTitle
                        ) {
                            return false;
                        }

                        if (
                            !favoriteBook
                        ) {
                            return true;
                        }

                        const catalogBook =
                            getBookKey(
                                catalogItem?.bookTitle
                            );

                        return (
                            !catalogBook ||
                            catalogBook ===
                            favoriteBook
                        );
                    }
                );

            if (byTitleBook) {
                return byTitleBook;
            }
        }


        // ------------------------------------------------------
        // TITLE-ONLY LAST RESORT
        // ------------------------------------------------------

        if (favoriteTitle) {
            return (
                catalogItems.find(
                    (catalogItem) =>
                        normalizeSearchText(
                            catalogItem?.title
                        ) ===
                        favoriteTitle
                ) ||
                null
            );
        }

        return null;
    }


    // ==========================================================
    // PIECES FAVORITES
    // ==========================================================

    function getWritingFavorites() {
        return sortFavorites(
            window.frMember
                .getFavorites()
        );
    }


    // ==========================================================
    // PIECES SEARCH
    // ==========================================================

    function getExternalSearchText(catalogItem) {
        const id =
            normalizeText(
                catalogItem?.id
            );

        if (!id) {
            return "";
        }

        return (
            searchTextByItemId.get(
                id
            ) ||
            ""
        );
    }


    function getWritingSearchHaystack(item) {
        const catalogItem =
            getCatalogItemForFavorite(
                item
            );

        return normalizeSearchText(
            [
                item?.title,
                item?.book,

                catalogItem?.title,
                catalogItem?.bookTitle,
                catalogItem?.description,
                catalogItem?.excerpt,
                catalogItem?.itemChapterName,
                /*
                 * Protected items may still carry searchText
                 * inside the access-gated item catalog.
                 */
                catalogItem?.searchText,

                /*
                 * Public full text comes from lazy-loaded
                 * per-book search files.
                 */
                getExternalSearchText(
                    catalogItem
                )
            ]
                .filter(Boolean)
                .join(" ")
        );
    }


    function writingMatchesBookFilter(item) {
        const itemBook =
            getBookKey(
                getBookName(
                    item
                )
            );

        return (
            !currentWritingBook ||
            itemBook ===
            currentWritingBook
        );
    }


    function writingMatchesFilters(item) {
        if (
            !writingMatchesBookFilter(
                item
            )
        ) {
            return false;
        }

        const search =
            normalizeSearchText(
                currentWritingSearch
            );

        if (!search) {
            return true;
        }

        return getWritingSearchHaystack(
            item
        ).includes(
            search
        );
    }


    function getFilteredWritingFavorites() {
        return getWritingFavorites()
            .filter(
                writingMatchesFilters
            );
    }


    async function loadSearchManifest() {
        if (searchManifestPromise) {
            return searchManifestPromise;
        }

        searchManifestPromise =
            fetch(
                SEARCH_MANIFEST_URL,
                {
                    credentials:
                        "same-origin"
                }
            )
                .then(
                    (response) => {

                        if (!response.ok) {
                            throw new Error(
                                `Search manifest request failed: ${response.status}`
                            );
                        }

                        return response.json();
                    }
                )
                .catch(
                    (error) => {

                        searchManifestPromise =
                            null;

                        console.warn(
                            "Unable to load Favorites search manifest:",
                            error
                        );

                        return {
                            books: []
                        };
                    }
                );

        return searchManifestPromise;
    }


    function cacheSearchCatalogItems(payload) {
        const items =
            Array.isArray(
                payload?.items
            )
                ? payload.items
                : [];

        items.forEach(
            (item) => {

                const id =
                    normalizeText(
                        item?.id
                    );

                if (!id) {
                    return;
                }

                searchTextByItemId.set(
                    id,
                    normalizeSearchText(
                        item?.text
                    )
                );
            }
        );
    }


    function loadSearchCatalog(entry) {
        const file =
            String(
                entry?.file ||
                ""
            ).trim();

        if (!file) {
            return Promise.resolve();
        }

        if (
            searchCatalogPromises.has(
                file
            )
        ) {
            return searchCatalogPromises.get(
                file
            );
        }

        const promise =
            fetch(
                file,
                {
                    credentials:
                        "same-origin"
                }
            )
                .then(
                    (response) => {

                        if (!response.ok) {
                            throw new Error(
                                `Search catalog request failed: ${response.status} ${file}`
                            );
                        }

                        return response.json();
                    }
                )
                .then(
                    (payload) => {

                        cacheSearchCatalogItems(
                            payload
                        );
                    }
                )
                .catch(
                    (error) => {

                        searchCatalogPromises.delete(
                            file
                        );

                        console.warn(
                            "Unable to load Favorites search catalog:",
                            error
                        );
                    }
                );

        searchCatalogPromises.set(
            file,
            promise
        );

        return promise;
    }


    function getWritingSearchBookSlugs() {
        const slugs =
            new Set();

        getWritingFavorites()
            .filter(
                writingMatchesBookFilter
            )
            .forEach(
                (item) => {

                    const catalogItem =
                        getCatalogItemForFavorite(
                            item
                        );

                    if (
                        !catalogItem ||
                        catalogItem.searchText
                    ) {
                        return;
                    }

                    const bookSlug =
                        normalizeText(
                            catalogItem.bookSlug
                        );

                    if (bookSlug) {
                        slugs.add(
                            bookSlug
                        );
                    }
                }
            );

        return slugs;
    }


    async function ensureWritingSearchCatalogs() {
        const search =
            normalizeSearchText(
                currentWritingSearch
            );

        if (!search) {
            return;
        }

        const neededBookSlugs =
            getWritingSearchBookSlugs();

        if (!neededBookSlugs.size) {
            return;
        }

        const manifest =
            await loadSearchManifest();

        const entries =
            Array.isArray(
                manifest?.books
            )
                ? manifest.books
                : [];

        const loads =
            entries
                .filter(
                    (entry) =>
                        neededBookSlugs.has(
                            normalizeText(
                                entry?.bookSlug
                            )
                        )
                )
                .map(
                    loadSearchCatalog
                );

        await Promise.all(
            loads
        );
    }


    async function refreshWritingSearch() {
        const requestId =
            ++writingSearchRequestId;

        const hasSearch =
            Boolean(
                normalizeSearchText(
                    currentWritingSearch
                )
            );

        if (hasSearch) {
            writingSearchLoading =
                true;

            renderWriting();
            updateCount();

            await ensureWritingSearchCatalogs();

            if (
                requestId !==
                writingSearchRequestId
            ) {
                return;
            }
        }

        writingSearchLoading =
            false;

        renderWriting();
        updateCount();
    }


    // ==========================================================
    // PIECES BOOK PICKER
    // ==========================================================

    function populateWritingBookOptions() {
        if (
            !writingBookSelect
        ) {
            return;
        }

        const favorites =
            getWritingFavorites();

        const books =
            new Map();

        favorites.forEach(
            (item) => {

                const label =
                    getBookName(
                        item
                    );

                const key =
                    getBookKey(
                        label
                    );

                if (
                    key &&
                    !books.has(key)
                ) {
                    books.set(
                        key,
                        label
                    );
                }
            }
        );


        const entries =
            Array.from(
                books.entries()
            )
                .sort(
                    (
                        [, a],
                        [, b]
                    ) =>
                        a.localeCompare(
                            b,
                            undefined,
                            {
                                sensitivity:
                                    "base"
                            }
                        )
                );


        writingBookSelect.innerHTML =
            "";


        const allOption =
            document.createElement(
                "option"
            );

        allOption.value =
            "";

        allOption.textContent =
            "All Books";

        writingBookSelect.appendChild(
            allOption
        );


        entries.forEach(
            ([key, label]) => {

                const option =
                    document.createElement(
                        "option"
                    );

                option.value =
                    key;

                option.textContent =
                    label;

                writingBookSelect.appendChild(
                    option
                );
            }
        );


        const validKeys =
            new Set(
                entries.map(
                    ([key]) =>
                        key
                )
            );


        if (
            currentWritingBook &&
            validKeys.has(
                currentWritingBook
            )
        ) {
            writingBookSelect.value =
                currentWritingBook;

            return;
        }


        currentWritingBook =
            "";

        writingBookSelect.value =
            "";

        writeCollectionState({
            writingBook: ""
        });
    }


    function setWritingBookFilter(value) {
        currentWritingBook =
            getBookKey(
                value
            );

        writeCollectionState({
            writingBook:
            currentWritingBook
        });

        refreshWritingSearch();
    }


    // ==========================================================
    // PIECES SEARCH CONTROLS
    // ==========================================================

    function setWritingSearch(value) {
        currentWritingSearch =
            String(
                value || ""
            );

        writeCollectionState({
            writingSearch:
            currentWritingSearch
        });

        updateWritingSearchClearButton();

        refreshWritingSearch();
    }


    function updateWritingSearchClearButton() {
        if (
            !writingSearchClear
        ) {
            return;
        }

        const hasSearch =
            Boolean(
                currentWritingSearch
                    .trim()
            );

        writingSearchClear.hidden =
            false;

        writingSearchClear.disabled =
            !hasSearch;

        writingSearchClear.setAttribute(
            "aria-disabled",
            hasSearch
                ? "false"
                : "true"
        );
    }


    function clearWritingSearch() {
        currentWritingSearch =
            "";

        if (
            writingSearchInput
        ) {
            writingSearchInput.value =
                "";
        }

        writeCollectionState({
            writingSearch: ""
        });

        updateWritingSearchClearButton();

        refreshWritingSearch();

        writingSearchInput
            ?.focus();
    }


    // ==========================================================
    // PENDING PIECE REMOVALS
    // ==========================================================

    function savePendingWritingRemovalIds() {
        writeCollectionState({
            pendingWritingRemovalIds:
                Array.from(
                    pendingWritingRemovalIds
                )
        });
    }


    function restorePendingWritingRemovalIds() {
        const saved =
            readCollectionState();

        const ids =
            Array.isArray(
                saved.pendingWritingRemovalIds
            )
                ? saved.pendingWritingRemovalIds
                : [];

        pendingWritingRemovalIds =
            new Set(
                ids
                    .map(
                        (id) =>
                            String(
                                id || ""
                            ).trim()
                    )
                    .filter(Boolean)
            );
    }


    function isWritingPendingRemoval(
        postId
    ) {
        const id =
            String(
                postId || ""
            ).trim();

        return Boolean(
            id &&
            pendingWritingRemovalIds
                .has(id)
        );
    }


    function toggleWritingPendingRemoval(
        postId
    ) {
        const id =
            String(
                postId || ""
            ).trim();

        if (!id) {
            return;
        }

        if (
            pendingWritingRemovalIds
                .has(id)
        ) {
            pendingWritingRemovalIds
                .delete(id);

        } else {
            pendingWritingRemovalIds
                .add(id);
        }

        savePendingWritingRemovalIds();

        renderWriting();
    }


    function cleanPendingWritingRemovalIds() {
        const validIds =
            new Set(
                getWritingFavorites()
                    .map(
                        (item) =>
                            String(
                                item?.postId ||
                                ""
                            ).trim()
                    )
                    .filter(Boolean)
            );

        let changed =
            false;

        Array.from(
            pendingWritingRemovalIds
        )
            .forEach(
                (id) => {

                    if (
                        !validIds.has(id)
                    ) {
                        pendingWritingRemovalIds
                            .delete(id);

                        changed =
                            true;
                    }
                }
            );

        if (changed) {
            savePendingWritingRemovalIds();
        }
    }


    function commitWritingRemoval(
        postId
    ) {
        const id =
            String(
                postId || ""
            ).trim();

        if (
            !id ||
            !pendingWritingRemovalIds
                .has(id)
        ) {
            return;
        }

        if (
            window.frMember
                .isFavorite(id)
        ) {
            window.frMember
                .toggleFavorite(id);
        }

        pendingWritingRemovalIds
            .delete(id);

        savePendingWritingRemovalIds();

        populateWritingBookOptions();

        renderWriting();

        updateCount();
    }


    // ==========================================================
    // PIECE HTML
    // ==========================================================

    function renderItem(item) {
        const rawPostId =
            String(
                item?.postId || ""
            ).trim();

        const postId =
            escapeHtml(
                rawPostId
            );

        const title =
            escapeHtml(
                item?.title ||
                "Untitled"
            );

        const url =
            escapeHtml(
                item?.url ||
                "#"
            );

        const pages =
            Math.max(
                1,
                Number(
                    item?.pages
                ) ||
                Number(
                    getCatalogItemForFavorite(
                        item
                    )?.pageCount
                ) ||
                1
            );

        const pageLabel =
            pages === 1
                ? "1 page"
                : `${pages} pages`;

        const pending =
            isWritingPendingRemoval(
                rawPostId
            );

        return `
            <div
                class="fr-collection__item${pending ? " is-pending-remove" : ""}"
                data-collection-writing-item
                data-collection-writing-id="${postId}"
            >

                <div class="fr-collection__item-title">

                    <a href="${url}">
                        ${title}
                    </a>

                    <span
                        class="fr-collection__leaders"
                        aria-hidden="true"
                    ></span>

                </div>


                <div class="fr-collection__pages">
                    ${pageLabel}
                </div>


                <div class="fr-collection__itemActions">

                    <button
                        class="fr-collection__itemHeart"
                        type="button"
                        aria-label="${
            pending
                ? "Keep this piece in Favorites"
                : "Mark this piece for removal from Favorites"
        }"
                        title="${
            pending
                ? "Keep in Favorites"
                : "Remove from Favorites"
        }"
                        aria-pressed="${
            pending
                ? "true"
                : "false"
        }"
                        data-collection-writing-remove="${postId}"
                    >
                        ♥
                    </button>

                    ${
            pending
                ? `
                                <button
                                    class="fr-collection__updateFavorite"
                                    type="button"
                                    data-collection-writing-update="${postId}"
                                >
                                    Remove
                                </button>
                            `
                : ""
        }

                </div>

            </div>
        `;
    }


    // ==========================================================
    // PIECES RENDER
    // ==========================================================

    function renderWriting() {
        cleanPendingWritingRemovalIds();

        const allFavorites =
            getWritingFavorites();

        if (
            writingSearchLoading
        ) {
            content.innerHTML = `
                <div class="fr-collection__empty">
                    Searching favorite pieces…
                </div>
            `;

            encouragement.hidden =
                true;

            return;
        }

        const favorites =
            allFavorites.filter(
                writingMatchesFilters
            );


        if (
            !allFavorites.length
        ) {
            content.innerHTML = `
                <div class="fr-collection__empty">
                    Your favorite selections will appear here.
                </div>
            `;

            encouragement.hidden =
                true;

            return;
        }


        if (
            !favorites.length
        ) {
            content.innerHTML = `
                <div class="fr-collection__empty">
                    No favorite pieces match these filters.
                </div>
            `;

            encouragement.hidden =
                true;

            return;
        }


        const groups =
            groupByBook(
                favorites
            );

        let html =
            "";


        groups.forEach(
            (
                items,
                bookName
            ) => {

                html += `
                    <section
                        class="fr-collection__bookGroup"
                    >

                        <h2 class="fr-collection__book">
                            ${escapeHtml(bookName)}
                        </h2>

                        ${
                    items
                        .map(
                            renderItem
                        )
                        .join("")
                }

                    </section>
                `;
            }
        );


        content.innerHTML =
            html;

        encouragement.hidden =
            favorites.length >
            10;
    }


    // ==========================================================
    // QUOTE FAVORITES STORAGE
    // ==========================================================

    function readQuoteFavorites() {
        try {
            if (window.tbMemberReading) return window.tbMemberReading.getQuoteFavorites();
            const raw =
                window.localStorage.getItem(
                    QUOTE_FAVORITES_STORAGE_KEY
                );

            if (!raw) {
                return [];
            }

            const parsed =
                JSON.parse(raw);

            return Array.isArray(parsed)
                ? parsed
                : [];

        } catch (error) {
            console.warn(
                "Unable to read favorite quotes:",
                error
            );

            return [];
        }
    }


    function writeQuoteFavorites(items) {
        try {
            if (window.tbMemberReading) {
                window.tbMemberReading.setQuoteFavorites(items);
            } else {
                window.localStorage.setItem(
                    QUOTE_FAVORITES_STORAGE_KEY,
                    JSON.stringify(items)
                );
            }

            window.dispatchEvent(
                new CustomEvent(
                    "fr-books-quote-favorites-changed"
                )
            );

        } catch (error) {
            console.warn(
                "Unable to save favorite quotes:",
                error
            );
        }
    }


    function normalizeQuoteItem(item) {
        if (
            !item ||
            typeof item !==
            "object"
        ) {
            return null;
        }

        const text =
            normalizeText(
                item.text
            );

        if (!text) {
            return null;
        }

        return {
            ...item,

            id:
                normalizeText(
                    item.id
                ) ||
                text,

            text,

            book:
                normalizeText(
                    item.book
                ) ||
                normalizeText(
                    item.bookTitle
                ) ||
                "Other",

            sourceTitle:
                normalizeText(
                    item.sourceTitle ||
                    item.chapterTitle ||
                    item.title
                ),

            url:
                String(
                    item.url || "#"
                )
        };
    }


    function getQuoteFavorites() {
        return readQuoteFavorites()
            .map(
                normalizeQuoteItem
            )
            .filter(Boolean);
    }


    function getQuoteBookName(item) {
        return (
            normalizeText(
                item?.book
            ) ||
            "Other"
        );
    }


    function getQuoteBookKey(value) {
        return normalizeSearchText(
            value
        );
    }


    function getQuoteId(item) {
        return normalizeText(
            item?.id
        );
    }


    function sortQuotes(items) {
        return [...items].sort(
            (a, b) => {

                const bookCompare =
                    getQuoteBookName(a)
                        .localeCompare(
                            getQuoteBookName(b),
                            undefined,
                            {
                                sensitivity:
                                    "base"
                            }
                        );

                if (
                    bookCompare !== 0
                ) {
                    return bookCompare;
                }

                return String(
                    a?.sourceTitle ||
                    a?.title ||
                    ""
                ).localeCompare(
                    String(
                        b?.sourceTitle ||
                        b?.title ||
                        ""
                    ),
                    undefined,
                    {
                        sensitivity:
                            "base"
                    }
                );
            }
        );
    }


    // ==========================================================
    // QUOTES SEARCH
    // ==========================================================

    function quoteMatchesFilters(item) {
        const itemBook =
            getQuoteBookKey(
                getQuoteBookName(
                    item
                )
            );

        if (
            currentQuoteBook &&
            itemBook !==
            currentQuoteBook
        ) {
            return false;
        }

        const search =
            normalizeSearchText(
                currentQuoteSearch
            );

        if (!search) {
            return true;
        }

        const haystack =
            normalizeSearchText(
                [
                    item?.text,
                    item?.book,
                    item?.sourceTitle,
                    item?.title
                ]
                    .filter(Boolean)
                    .join(" ")
            );

        return haystack.includes(
            search
        );
    }


    function getFilteredQuoteFavorites() {
        return sortQuotes(
            getQuoteFavorites()
        ).filter(
            quoteMatchesFilters
        );
    }


    // ==========================================================
    // QUOTES BOOK PICKER
    // ==========================================================

    function populateQuoteBookOptions() {
        if (
            !quotesBookSelect
        ) {
            return;
        }

        const books =
            new Map();

        getQuoteFavorites()
            .forEach(
                (item) => {

                    const label =
                        getQuoteBookName(
                            item
                        );

                    const key =
                        getQuoteBookKey(
                            label
                        );

                    if (
                        key &&
                        !books.has(key)
                    ) {
                        books.set(
                            key,
                            label
                        );
                    }
                }
            );


        const entries =
            Array.from(
                books.entries()
            )
                .sort(
                    (
                        [, a],
                        [, b]
                    ) =>
                        a.localeCompare(
                            b,
                            undefined,
                            {
                                sensitivity:
                                    "base"
                            }
                        )
                );


        quotesBookSelect.innerHTML =
            "";


        const allOption =
            document.createElement(
                "option"
            );

        allOption.value =
            "";

        allOption.textContent =
            "All Books";

        quotesBookSelect.appendChild(
            allOption
        );


        entries.forEach(
            ([key, label]) => {

                const option =
                    document.createElement(
                        "option"
                    );

                option.value =
                    key;

                option.textContent =
                    label;

                quotesBookSelect.appendChild(
                    option
                );
            }
        );


        const validKeys =
            new Set(
                entries.map(
                    ([key]) =>
                        key
                )
            );


        if (
            currentQuoteBook &&
            validKeys.has(
                currentQuoteBook
            )
        ) {
            quotesBookSelect.value =
                currentQuoteBook;

            return;
        }


        currentQuoteBook =
            "";

        quotesBookSelect.value =
            "";

        writeCollectionState({
            quoteBook: ""
        });
    }


    function setQuoteBookFilter(value) {
        currentQuoteBook =
            getQuoteBookKey(
                value
            );

        writeCollectionState({
            quoteBook:
            currentQuoteBook
        });

        renderQuotes();

        updateCount();
    }


    // ==========================================================
    // QUOTES SEARCH CONTROLS
    // ==========================================================

    function setQuoteSearch(value) {
        currentQuoteSearch =
            String(
                value || ""
            );

        writeCollectionState({
            quoteSearch:
            currentQuoteSearch
        });

        updateQuoteSearchClearButton();

        renderQuotes();

        updateCount();
    }


    function updateQuoteSearchClearButton() {
        if (
            !quotesSearchClear
        ) {
            return;
        }

        const hasSearch =
            Boolean(
                currentQuoteSearch
                    .trim()
            );

        quotesSearchClear.hidden =
            false;

        quotesSearchClear.disabled =
            !hasSearch;

        quotesSearchClear.setAttribute(
            "aria-disabled",
            hasSearch
                ? "false"
                : "true"
        );
    }


    function clearQuoteSearch() {
        currentQuoteSearch =
            "";

        if (
            quotesSearchInput
        ) {
            quotesSearchInput.value =
                "";
        }

        writeCollectionState({
            quoteSearch: ""
        });

        updateQuoteSearchClearButton();

        renderQuotes();

        updateCount();

        quotesSearchInput
            ?.focus();
    }


    // ==========================================================
    // PENDING QUOTE REMOVALS
    // ==========================================================

    function savePendingQuoteRemovalIds() {
        writeCollectionState({
            pendingQuoteRemovalIds:
                Array.from(
                    pendingQuoteRemovalIds
                )
        });
    }


    function restorePendingQuoteRemovalIds() {
        const saved =
            readCollectionState();

        const ids =
            Array.isArray(
                saved.pendingQuoteRemovalIds
            )
                ? saved.pendingQuoteRemovalIds
                : [];

        pendingQuoteRemovalIds =
            new Set(
                ids
                    .map(
                        (id) =>
                            String(
                                id || ""
                            ).trim()
                    )
                    .filter(Boolean)
            );
    }


    function isQuotePendingRemoval(
        quoteId
    ) {
        const id =
            String(
                quoteId || ""
            ).trim();

        return Boolean(
            id &&
            pendingQuoteRemovalIds
                .has(id)
        );
    }


    function toggleQuotePendingRemoval(
        quoteId
    ) {
        const id =
            String(
                quoteId || ""
            ).trim();

        if (!id) {
            return;
        }

        if (
            pendingQuoteRemovalIds
                .has(id)
        ) {
            pendingQuoteRemovalIds
                .delete(id);

        } else {
            pendingQuoteRemovalIds
                .add(id);
        }

        savePendingQuoteRemovalIds();

        renderQuotes();
    }


    function cleanPendingQuoteRemovalIds() {
        const validIds =
            new Set(
                getQuoteFavorites()
                    .map(
                        getQuoteId
                    )
                    .filter(Boolean)
            );

        let changed =
            false;

        Array.from(
            pendingQuoteRemovalIds
        )
            .forEach(
                (id) => {

                    if (
                        !validIds.has(id)
                    ) {
                        pendingQuoteRemovalIds
                            .delete(id);

                        changed =
                            true;
                    }
                }
            );

        if (changed) {
            savePendingQuoteRemovalIds();
        }
    }


    function commitQuoteRemoval(
        quoteId
    ) {
        const id =
            String(
                quoteId || ""
            ).trim();

        if (
            !id ||
            !pendingQuoteRemovalIds
                .has(id)
        ) {
            return;
        }

        const next =
            getQuoteFavorites()
                .filter(
                    (item) =>
                        getQuoteId(
                            item
                        ) !==
                        id
                );

        writeQuoteFavorites(
            next
        );

        pendingQuoteRemovalIds
            .delete(id);

        savePendingQuoteRemovalIds();

        populateQuoteBookOptions();

        renderQuotes();

        updateCount();
    }


    // ==========================================================
    // QUOTE HTML
    // ==========================================================

    function renderQuote(item) {
        const rawQuoteId =
            getQuoteId(
                item
            );

        const quoteId =
            escapeHtml(
                rawQuoteId
            );

        const text =
            escapeHtml(
                item?.text ||
                ""
            );

        const sourceTitle =
            escapeHtml(
                item?.sourceTitle ||
                item?.title ||
                ""
            );

        const book =
            escapeHtml(
                getQuoteBookName(
                    item
                )
            );

        const url =
            escapeHtml(
                item?.url ||
                "#"
            );

        const pending =
            isQuotePendingRemoval(
                rawQuoteId
            );

        return `
            <article
                class="fr-collection-quote${pending ? " is-pending-remove" : ""}"
                data-collection-quote
                data-collection-quote-id="${quoteId}"
            >

                <div class="fr-collection-quote__content">

                    <blockquote class="fr-collection-quote__text">
                        “${text}”
                    </blockquote>

                    <p class="fr-collection-quote__source">

                        ${
            sourceTitle
                ? `
                                    <a
                                        class="fr-collection-quote__sourceTitle"
                                        href="${url}"
                                    >
                                        ${sourceTitle}
                                    </a>
                                `
                : ""
        }

                        ${
            book
                ? `
                                    <span class="fr-collection-quote__book">
                                        ${book}
                                    </span>
                                `
                : ""
        }

                    </p>

                </div>


                <div class="fr-collection-quote__actions">

                    <button
                        class="fr-collection-quote__remove"
                        type="button"
                        aria-label="${
            pending
                ? "Keep this quote in Favorites"
                : "Mark this quote for removal from Favorites"
        }"
                        title="${
            pending
                ? "Keep in Favorites"
                : "Remove from Favorites"
        }"
                        aria-pressed="${
            pending
                ? "true"
                : "false"
        }"
                        data-collection-quote-remove="${quoteId}"
                    >
                        ♥
                    </button>

                    ${
            pending
                ? `
                                <button
                                    class="fr-collection__updateFavorite"
                                    type="button"
                                    data-collection-quote-update="${quoteId}"
                                >
                                    Remove
                                </button>
                            `
                : ""
        }

                </div>

            </article>
        `;
    }


    // ==========================================================
    // QUOTES RENDER
    // ==========================================================

    function renderQuotes() {
        if (!quotesContent) {
            return;
        }

        cleanPendingQuoteRemovalIds();

        const allQuotes =
            getQuoteFavorites();

        const favorites =
            getFilteredQuoteFavorites();


        if (
            !allQuotes.length
        ) {
            quotesContent.innerHTML = `
                <div class="fr-collection__empty">
                    Your favorite quotes will appear here.
                </div>
            `;

            return;
        }


        if (
            !favorites.length
        ) {
            quotesContent.innerHTML = `
                <div class="fr-collection__empty">
                    No favorite quotes match these filters.
                </div>
            `;

            return;
        }


        quotesContent.innerHTML =
            favorites
                .map(
                    renderQuote
                )
                .join("");
    }


    // ==========================================================
    // RESTORE FILTER STATE
    // ==========================================================

    function restoreWritingFilterState() {
        const saved =
            readCollectionState();

        currentWritingBook =
            typeof saved.writingBook ===
            "string"
                ? getBookKey(
                    saved.writingBook
                )
                : "";

        currentWritingSearch =
            typeof saved.writingSearch ===
            "string"
                ? saved.writingSearch
                : "";

        if (
            writingSearchInput
        ) {
            writingSearchInput.value =
                currentWritingSearch;
        }

        updateWritingSearchClearButton();
    }


    function restoreQuoteFilterState() {
        const saved =
            readCollectionState();

        currentQuoteBook =
            typeof saved.quoteBook ===
            "string"
                ? normalizeText(
                    saved.quoteBook
                )
                : "";

        currentQuoteSearch =
            typeof saved.quoteSearch ===
            "string"
                ? saved.quoteSearch
                : "";

        if (
            quotesSearchInput
        ) {
            quotesSearchInput.value =
                currentQuoteSearch;
        }

        updateQuoteSearchClearButton();
    }


    // ==========================================================
    // COUNT
    // ==========================================================

    function updateCount() {
        if (
            currentView ===
            "quotes"
        ) {
            const total =
                getFilteredQuoteFavorites()
                    .length;

            count.textContent =
                total === 1
                    ? "1 quote"
                    : `${total} quotes`;

            return;
        }

        if (
            writingSearchLoading
        ) {
            count.textContent =
                "Searching…";

            return;
        }

        const total =
            getFilteredWritingFavorites()
                .length;

        count.textContent =
            total === 1
                ? "1 selection"
                : `${total} selections`;
    }


    // ==========================================================
    // VIEW
    // ==========================================================

    function setView(
        view,
        save = true
    ) {
        if (
            view !==
            "writing" &&
            view !==
            "quotes"
        ) {
            view =
                "writing";
        }

        currentView =
            view;

        collection.setAttribute(
            "data-collection-view",
            view
        );

        tabs.forEach(
            (tab) => {

                const active =
                    tab.getAttribute(
                        "data-collection-tab"
                    ) ===
                    view;

                tab.classList.toggle(
                    "is-active",
                    active
                );

                tab.setAttribute(
                    "aria-selected",
                    active
                        ? "true"
                        : "false"
                );

                tab.setAttribute(
                    "tabindex",
                    active
                        ? "0"
                        : "-1"
                );
            }
        );

        panels.forEach(
            (panel) => {

                const active =
                    panel.getAttribute(
                        "data-collection-panel"
                    ) ===
                    view;

                panel.hidden =
                    !active;

                panel.setAttribute(
                    "aria-hidden",
                    active
                        ? "false"
                        : "true"
                );
            }
        );

        if (
            view ===
            "writing"
        ) {
            populateWritingBookOptions();

            refreshWritingSearch();

        } else {
            populateQuoteBookOptions();

            renderQuotes();
        }

        updateCount();

        updateWritingSearchClearButton();

        updateQuoteSearchClearButton();

        if (save) {
            writeCollectionState({
                view
            });
        }
    }


    // ==========================================================
    // TAB EVENTS
    // ==========================================================

    tabs.forEach(
        (tab) => {

            tab.addEventListener(
                "click",
                () => {

                    setView(
                        tab.getAttribute(
                            "data-collection-tab"
                        )
                    );
                }
            );
        }
    );


    // ==========================================================
    // PIECES FILTER EVENTS
    // ==========================================================

    if (
        writingBookSelect
    ) {
        writingBookSelect.addEventListener(
            "change",
            () => {

                setWritingBookFilter(
                    writingBookSelect.value
                );
            }
        );
    }


    if (
        writingSearchInput
    ) {
        writingSearchInput.addEventListener(
            "input",
            () => {

                setWritingSearch(
                    writingSearchInput.value
                );
            }
        );
    }


    if (
        writingSearchClear
    ) {
        writingSearchClear.addEventListener(
            "click",
            clearWritingSearch
        );
    }


    // ==========================================================
    // QUOTE FILTER EVENTS
    // ==========================================================

    if (
        quotesBookSelect
    ) {
        quotesBookSelect.addEventListener(
            "change",
            () => {

                setQuoteBookFilter(
                    quotesBookSelect.value
                );
            }
        );
    }


    if (
        quotesSearchInput
    ) {
        quotesSearchInput.addEventListener(
            "input",
            () => {

                setQuoteSearch(
                    quotesSearchInput.value
                );
            }
        );
    }


    if (
        quotesSearchClear
    ) {
        quotesSearchClear.addEventListener(
            "click",
            clearQuoteSearch
        );
    }


    // ==========================================================
    // PIECE FAVORITE EVENTS
    // ==========================================================

    content.addEventListener(
        "click",
        (event) => {

            const heartButton =
                event.target.closest(
                    "[data-collection-writing-remove]"
                );

            if (heartButton) {
                event.preventDefault();

                event.stopPropagation();

                toggleWritingPendingRemoval(
                    heartButton.getAttribute(
                        "data-collection-writing-remove"
                    )
                );

                return;
            }


            const removeButton =
                event.target.closest(
                    "[data-collection-writing-update]"
                );

            if (
                !removeButton
            ) {
                return;
            }

            event.preventDefault();

            event.stopPropagation();

            commitWritingRemoval(
                removeButton.getAttribute(
                    "data-collection-writing-update"
                )
            );
        }
    );


    // ==========================================================
    // QUOTE FAVORITE EVENTS
    // ==========================================================

    if (
        quotesContent
    ) {
        quotesContent.addEventListener(
            "click",
            (event) => {

                const heartButton =
                    event.target.closest(
                        "[data-collection-quote-remove]"
                    );

                if (heartButton) {
                    event.preventDefault();

                    event.stopPropagation();

                    toggleQuotePendingRemoval(
                        heartButton.getAttribute(
                            "data-collection-quote-remove"
                        )
                    );

                    return;
                }


                const removeButton =
                    event.target.closest(
                        "[data-collection-quote-update]"
                    );

                if (
                    !removeButton
                ) {
                    return;
                }

                event.preventDefault();

                event.stopPropagation();

                commitQuoteRemoval(
                    removeButton.getAttribute(
                        "data-collection-quote-update"
                    )
                );
            }
        );
    }


    // ==========================================================
    // MEMBER DATA CHANGES
    // ==========================================================

    document.addEventListener(
        "tb-member-reading-changed",
        () => {
            populateWritingBookOptions();
            refreshWritingSearch();
        }
    );

    window.addEventListener(
        "storage",
        (event) => {

            const memberId =
                window.frMember
                    .getId();

            if (memberId) {
                const memberKey =
                    `fr-member:${memberId}`;

                if (
                    event.key ===
                    memberKey
                ) {
                    populateWritingBookOptions();

                    refreshWritingSearch();
                }
            }

            if (
                event.key ===
                QUOTE_FAVORITES_STORAGE_KEY
            ) {
                populateQuoteBookOptions();

                renderQuotes();

                if (
                    currentView ===
                    "quotes"
                ) {
                    updateCount();
                }
            }
        }
    );


    window.addEventListener(
        "fr-books-quote-favorites-changed",
        () => {

            populateQuoteBookOptions();

            renderQuotes();

            if (
                currentView ===
                "quotes"
            ) {
                updateCount();
            }
        }
    );


    // ==========================================================
    // INITIAL DISPLAY
    // ==========================================================

    restorePendingWritingRemovalIds();

    restorePendingQuoteRemovalIds();

    restoreWritingFilterState();

    restoreQuoteFilterState();

    const savedState =
        readCollectionState();

    const savedView =
        savedState.view ===
        "quotes"
            ? "quotes"
            : "writing";

    populateWritingBookOptions();

    populateQuoteBookOptions();

    renderQuotes();

    setView(
        savedView,
        false
    );

})();
